import { inferDatabaseEngine } from '@pkg/db/utils'
import { logger } from '@pkg/shared/logger'
import { type CompiledQuery, type Kysely, type MigrationProvider, Migrator, sql } from 'kysely'
import type { AppMigratorProvider, MigrationContext } from './AppMigratorProvider'

export interface AppMigratorConfig {
  provider: MigrationProvider | AppMigratorProvider
  // biome-ignore lint/suspicious/noExplicitAny: migrations are not tied to the schema
  db: Kysely<any>
}

export class AppMigrator {
  private readonly log = logger.child({ scope: 'db:migrate' })

  constructor(private readonly config: AppMigratorConfig) {}

  // biome-ignore lint/suspicious/noExplicitAny: migrations are not tied to the schema
  private get db(): Kysely<any> {
    return this.config.db
  }

  async migrateToLatest() {
    this.log.info('Starting database migration.')

    const migrator = new Migrator({ db: this.db, provider: this.config.provider })

    const { error, results } = await migrator.migrateToLatest()
    for (const it of results ?? []) {
      const status = it.status === 'Success' ? 'success' : it.status === 'Error' ? 'error' : 'skipped'
      this.log.info({ migration: it.migrationName, status }, `${it.migrationName}: ${status}`)
    }

    if (error) {
      this.log.error({ err: error, results }, 'Failed to run migrations')
      throw error as Error
    }

    await this.migrateRepeatableToLatest()

    return { error, results }
  }

  private async ensureRepeatableTrackingTable(): Promise<void> {
    await this.db.schema
      .createTable('kysely_migration_repeatable')
      .ifNotExists()
      .addColumn('name', 'varchar(255)', col => col.primaryKey())
      .addColumn('hash', 'varchar(64)', col => col.notNull())
      .addColumn('executed_at', 'timestamp', col => col.notNull().defaultTo(sql`CURRENT_TIMESTAMP`))
      .execute()
  }

  private async migrateRepeatableToLatest(): Promise<void> {
    if (!('getRepeatableMigrations' in this.config.provider)) {
      return
    }

    await this.ensureRepeatableTrackingTable()

    const repeatableMigrations = await this.config.provider.getRepeatableMigrations()
    if (repeatableMigrations.length === 0) {
      return
    }

    this.log.info('Starting repeatable migrations')

    const engine = inferDatabaseEngine(this.db)
    const context = {
      scope: engine === 'postgres' ? 'server' : 'client',
      engine,
    } as MigrationContext

    for (const migration of repeatableMigrations) {
      const { repeatable } = migration

      let shouldExecute = false
      let reason = ''

      if (repeatable === true) {
        shouldExecute = true
        reason = 'always'
      } else if (typeof repeatable === 'string') {
        const stored = await this.db
          .selectFrom('kysely_migration_repeatable')
          .select(['hash'])
          .where('name', '=', migration.name)
          .executeTakeFirst()

        if (!stored) {
          shouldExecute = true
          reason = 'new'
        } else if (stored.hash !== repeatable) {
          shouldExecute = true
          reason = 'changed'
        }
      }

      if (!shouldExecute) {
        this.log.info({ migration: migration.name }, `${migration.name}: skipped (unchanged)`)
        continue
      }

      try {
        this.log.info({ migration: migration.name, reason }, `Executing repeatable migration: ${migration.name}`)
        await migration.up(this.db, context)

        if (typeof repeatable === 'string') {
          await this.db
            .insertInto('kysely_migration_repeatable')
            .values({ name: migration.name, hash: repeatable, executed_at: new Date() })
            .onConflict(oc => oc.column('name').doUpdateSet({ hash: repeatable, executed_at: new Date() }))
            .execute()
        }

        this.log.info({ migration: migration.name }, `${migration.name}: success`)
      } catch (error) {
        this.log.error(
          { err: error as Error, migration: migration.name },
          `Failed repeatable migration: ${migration.name}`,
        )
        throw error
      }
    }
  }

  async execute<O>(query: CompiledQuery<O>) {
    return await this.db.executeQuery(query)
  }
}
